import type {
  ReportFormData,
  LocationField as LocationFieldType,
} from '@/screens/ReportCreation/reportCreation.types';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { YStack } from 'tamagui';
import { FieldError } from '../FieldError';
import type { ControllerRules } from '../formController.types';
import { LocationField } from './LocationField';

interface ControlledLocationFieldProps<FormValues extends FieldValues> {
  field: LocationFieldType;
  formController: {
    control: Control<FormValues>;
    latitudeName: Path<FormValues>;
    longitudeName: Path<FormValues>;
    rules?: ControllerRules<FormValues>;
  };
}

export const ControlledLocationField = <
  FormValues extends FieldValues = ReportFormData,
>({
  field,
  formController,
}: ControlledLocationFieldProps<FormValues>): React.ReactElement => {
  const { field: latitudeField, fieldState: latitudeFieldState } =
    useController<FormValues>({
      name: formController.latitudeName,
      control: formController.control,
      rules: formController.rules,
    });

  const { field: longitudeField, fieldState: longitudeFieldState } =
    useController<FormValues>({
      name: formController.longitudeName,
      control: formController.control,
      rules: formController.rules,
    });

  const locationError =
    latitudeFieldState.error?.message ?? longitudeFieldState.error?.message;

  const latitudeValue =
    typeof latitudeField.value === 'string' ? latitudeField.value : '';
  const longitudeValue =
    typeof longitudeField.value === 'string' ? longitudeField.value : '';

  return (
    <YStack gap="$1">
      <LocationField
        label={field.label}
        latitude={latitudeValue}
        longitude={longitudeValue}
        onLatitudeChange={latitudeField.onChange}
        onLongitudeChange={longitudeField.onChange}
        onPinPress={() => {
          console.warn('Pin press - not yet implemented');
        }}
        latitudePlaceholder={field.latitudePlaceholder}
        longitudePlaceholder={field.longitudePlaceholder}
        required={field.required}
      />
      <FieldError message={locationError} />
    </YStack>
  );
};
